import React from 'react';
import { Link } from '@design-system/Link';
import { Button } from '@design-system/Button';
import { ChevronRightIcon } from '@/components/icons/ChevronRightIcon';
import { Skeleton } from '@design-system/Skeleton';
import { EventTracker } from '@/utils';

interface ContributingScoresSectionProps {
  contributingScoresCount: number;
  isLoading: boolean;
  scoreId: number;
  title: string;
  'data-testid'?: string;
}

const onAllContributingScores = (scoreId: number, title: string) => {
  EventTracker.trackEvent(
    'health',
    `${title.replace(/\s+/g, '')}_allContributingScores`,
  );
  return `/health/score/contributing-score/${scoreId}`;
};

export const ContributingScoresSection: React.FC<
  ContributingScoresSectionProps
> = ({ contributingScoresCount, isLoading, scoreId, title, 'data-testid': testId }) => {
  const scoreText = contributingScoresCount === 1 ? 'score' : 'scores';

  return (
    <div className="mt-6" data-testid={testId}>
      {isLoading ? (
        <>
          <Skeleton className="w-72 mb-2 h-10" data-testid="healthL2Details-contributingTitleSkeleton" />
          <Skeleton className="w-full h-10 mb-10" data-testid="healthL2Details-contributingDescriptionSkeleton" />
          <Skeleton className="w-full h-10 mb-2" data-testid="healthL2Details-contributingButtonSkeleton" />
        </>
      ) : (
        <>
          <h2 className="typography-h1 text-primary" data-testid="healthL2Details-contributingTitle">
            {`${contributingScoresCount} Contributing ${scoreText}`}
          </h2>
          <p className="typography-body text-secondary mt-2 max-w-[37rem]" data-testid="healthL2Details-contributingDescription">
            Dive deep into the underlying microbial functions and pathways that
            contribute to this Health Zone.
          </p>
          <div className="mt-4" data-testid="healthL2Details-contributingButtonContainer">
            <Button
              asChild
              variant="secondary"
              className="dark w-full h-12 [&_svg]:!size-6 justify-between pr-3 mt-6"
              data-testid="healthL2Details-contributingButton"
            >
              <Link
                className="dark"
                to={onAllContributingScores(scoreId, title)}
                data-testid="healthL2Details-contributingLink"
              >
                View all contributing scores
                <ChevronRightIcon height={24} width={24} />
              </Link>
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
